import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MediaAsset } from '@/domain/types';
import { deleteMediaAsset, listMediaAssets, uploadMediaAsset, type MediaUploadKind } from '@/services/media';

// ── Helpers ───────────────────────────────────────────────────────────────────

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif']);

function isImage(asset: MediaAsset) {
  return asset.mimeType?.startsWith('image/') || IMAGE_EXTS.has(asset.fileExtension?.toLowerCase() ?? '');
}

function fmtSize(bytes: number | null) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function fileIcon(asset: MediaAsset) {
  const ext = asset.fileExtension?.toLowerCase() ?? '';
  if (IMAGE_EXTS.has(ext))                      return 'ri-image-line';
  if (['zip', 'gz', 'tar', '7z'].includes(ext)) return 'ri-file-zip-line';
  if (['json', 'xml', 'yaml'].includes(ext))    return 'ri-code-line';
  if (ext === 'pdf')                            return 'ri-file-pdf-line';
  if (['js', 'ts', 'css', 'html'].includes(ext)) return 'ri-code-s-slash-line';
  return 'ri-file-3-line';
}

function fileIconColor(asset: MediaAsset) {
  const ext = asset.fileExtension?.toLowerCase() ?? '';
  if (IMAGE_EXTS.has(ext))                      return 'text-violet-500 bg-violet-50';
  if (['zip', 'gz', 'tar', '7z'].includes(ext)) return 'text-amber-500 bg-amber-50';
  if (['json', 'xml', 'yaml'].includes(ext))    return 'text-sky-500 bg-sky-50';
  if (ext === 'pdf')                            return 'text-red-500 bg-red-50';
  return 'text-surface-500 bg-surface-100';
}

// ── Upload queue item ─────────────────────────────────────────────────────────

interface UploadItem {
  id: string;
  name: string;
  size: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

// ── Drop zone ─────────────────────────────────────────────────────────────────

interface DropZoneProps {
  kind: MediaUploadKind;
  onKindChange: (k: MediaUploadKind) => void;
  isDragging: boolean;
  onFiles: (files: File[]) => void;
  onDragEnter: () => void;
  onDragLeave: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

function DropZone({ kind, onKindChange, isDragging, onFiles, onDragEnter, onDragLeave, inputRef }: DropZoneProps) {
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    onDragLeave();
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFiles(files);
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) onFiles(files);
    e.target.value = '';
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
      {/* Kind tabs */}
      <div className="flex border-b border-surface-200">
        {([
          ['public-media',   'ri-image-line',      'Public Media',    'Thumbnails, screenshots, images'],
          ['product-files',  'ri-file-zip-line',   'Product Files',   'Zip, JSON, installers (private)'],
        ] as [MediaUploadKind, string, string, string][]).map(([val, icon, label, desc]) => (
          <button
            key={val}
            onClick={() => onKindChange(val)}
            className={`flex-1 flex items-center gap-3 px-6 py-4 transition-colors text-left ${
              kind === val
                ? 'bg-brand-50 border-b-2 border-brand-600'
                : 'hover:bg-surface-50 border-b-2 border-transparent'
            }`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${kind === val ? 'bg-brand-100' : 'bg-surface-100'}`}>
              <i className={`${icon} text-lg ${kind === val ? 'text-brand-600' : 'text-surface-500'}`} />
            </div>
            <div>
              <p className={`text-sm font-semibold ${kind === val ? 'text-brand-700' : 'text-surface-700'}`}>{label}</p>
              <p className="text-xs text-surface-400">{desc}</p>
            </div>
            {kind === val && <i className="ri-checkbox-circle-fill text-brand-500 ml-auto text-lg shrink-0" />}
          </button>
        ))}
      </div>

      {/* Drop area */}
      <div
        onDragEnter={(e) => { e.preventDefault(); onDragEnter(); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={(e) => { e.preventDefault(); onDragLeave(); }}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-4 px-8 py-14 cursor-pointer transition-all select-none ${
          isDragging
            ? 'bg-brand-50 border-2 border-dashed border-brand-400'
            : 'bg-surface-50/50 border-2 border-dashed border-surface-200 hover:bg-surface-50 hover:border-brand-300'
        }`}
      >
        {isDragging ? (
          <>
            <div className="w-16 h-16 rounded-2xl bg-brand-100 flex items-center justify-center animate-bounce">
              <i className="ri-upload-cloud-2-line text-3xl text-brand-600" />
            </div>
            <p className="text-base font-semibold text-brand-600">Drop files here</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-2xl bg-white border border-surface-200 shadow-sm flex items-center justify-center">
              <i className="ri-upload-cloud-2-line text-3xl text-surface-400" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-surface-700 mb-1">
                Drag & drop files here, or <span className="text-brand-600">browse</span>
              </p>
              <p className="text-xs text-surface-400">
                {kind === 'public-media'
                  ? 'PNG, JPG, WebP, GIF, SVG — max 10 MB'
                  : 'ZIP, JSON, PDF, any file — max 50 MB'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-center">
              {(kind === 'public-media'
                ? ['PNG', 'JPG', 'WebP', 'GIF', 'SVG']
                : ['ZIP', 'JSON', 'PDF', 'JS', 'Any']
              ).map((fmt) => (
                <span key={fmt} className="px-2 py-0.5 rounded-md bg-white border border-surface-200 text-xs text-surface-500 font-medium">
                  {fmt}
                </span>
              ))}
            </div>
          </>
        )}
        <input ref={inputRef} type="file" multiple onChange={handleInput} className="hidden" />
      </div>
    </div>
  );
}

// ── Upload queue panel ────────────────────────────────────────────────────────

function UploadQueue({ items, onDismiss }: { items: UploadItem[]; onDismiss: () => void }) {
  if (items.length === 0) return null;
  const done    = items.filter((i) => i.status === 'done').length;
  const errors  = items.filter((i) => i.status === 'error').length;
  const active  = items.filter((i) => i.status === 'uploading' || i.status === 'pending').length;
  const allDone = active === 0;

  return (
    <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-surface-100">
        <div className="flex items-center gap-2.5">
          {active > 0
            ? <i className="ri-loader-4-line animate-spin text-brand-500" />
            : errors > 0
            ? <i className="ri-error-warning-line text-red-500" />
            : <i className="ri-checkbox-circle-line text-emerald-500" />}
          <span className="text-sm font-semibold text-surface-800">
            {active > 0 ? `Uploading ${done + 1} of ${items.length}…` : `${done} uploaded${errors > 0 ? `, ${errors} failed` : ''}`}
          </span>
        </div>
        {allDone && (
          <button onClick={onDismiss} className="text-xs text-surface-400 hover:text-surface-600 transition-colors">Dismiss</button>
        )}
      </div>
      <div className="divide-y divide-surface-50 max-h-52 overflow-y-auto">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 px-5 py-2.5">
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
              item.status === 'done'      ? 'bg-emerald-400'
              : item.status === 'error'  ? 'bg-red-400'
              : item.status === 'uploading' ? 'bg-brand-400 animate-pulse'
              : 'bg-surface-300'
            }`} />
            <span className="text-xs text-surface-700 flex-1 truncate">{item.name}</span>
            <span className="text-xs text-surface-400 shrink-0">{fmtSize(item.size)}</span>
            <span className={`text-xs font-medium shrink-0 capitalize ${
              item.status === 'done'     ? 'text-emerald-600'
              : item.status === 'error' ? 'text-red-500'
              : item.status === 'uploading' ? 'text-brand-600'
              : 'text-surface-400'
            }`}>
              {item.status === 'error' ? (item.error ?? 'Error') : item.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

function Lightbox({ asset, images, onClose, onCopy, onDelete, copied }: {
  asset: MediaAsset;
  images: MediaAsset[];
  onClose: () => void;
  onCopy: (a: MediaAsset) => void;
  onDelete: (a: MediaAsset) => void;
  copied: boolean;
}) {
  const idx  = images.findIndex((a) => a.id === asset.id);
  const prev = images[idx - 1] ?? null;
  const next = images[idx + 1] ?? null;

  // Close on Escape, navigate with arrow keys
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col bg-black/95 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-5 py-3.5 bg-black/60 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md shrink-0 ${asset.visibility === 'public' ? 'bg-emerald-500 text-white' : 'bg-surface-600 text-white'}`}>
            {asset.visibility === 'public' ? 'PUBLIC' : 'PRIVATE'}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{asset.displayName}</p>
            <p className="text-xs text-white/50">{fmtSize(asset.fileSize)} · {asset.fileExtension?.toUpperCase()}</p>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0 ml-4">
          <span className="text-xs text-white/40 mr-2">{idx + 1} / {images.length}</span>
          {asset.publicUrl && (
            <button
              onClick={() => onCopy(asset)}
              title="Copy URL"
              className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            >
              {copied ? <i className="ri-check-line text-emerald-400" /> : <i className="ri-link" />}
            </button>
          )}
          {asset.publicUrl && (
            <a
              href={asset.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open original"
              onClick={(e) => e.stopPropagation()}
              className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-white/10 text-white/70 hover:text-white transition-colors"
            >
              <i className="ri-external-link-line" />
            </a>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(asset); }}
            title="Delete"
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-red-500/20 text-white/70 hover:text-red-400 transition-colors"
          >
            <i className="ri-delete-bin-line" />
          </button>
          <div className="w-px h-5 bg-white/10 mx-1" />
          <button
            onClick={onClose}
            title="Close (Esc)"
            className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 flex items-center justify-center relative min-h-0 px-16" onClick={onClose}>
        <img
          src={asset.publicUrl ?? ''}
          alt={asset.displayName}
          className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          draggable={false}
        />

        {/* Prev */}
        {prev && (
          <button
            onClick={(e) => { e.stopPropagation(); /* handled by parent nav */ }}
            className="absolute left-3 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center text-white transition-colors"
            title="Previous"
            onClickCapture={(e) => { e.stopPropagation(); document.dispatchEvent(new CustomEvent('lightbox-prev')); }}
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
        )}

        {/* Next */}
        {next && (
          <button
            onClick={(e) => e.stopPropagation()}
            className="absolute right-3 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center text-white transition-colors"
            title="Next"
            onClickCapture={(e) => { e.stopPropagation(); document.dispatchEvent(new CustomEvent('lightbox-next')); }}
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        )}
      </div>

      {/* Bottom strip — thumbnail strip */}
      {images.length > 1 && (
        <div
          className="flex items-center gap-2 px-5 py-3 bg-black/60 overflow-x-auto shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, i) => (
            <button
              key={img.id}
              onClick={(e) => { e.stopPropagation(); document.dispatchEvent(new CustomEvent('lightbox-goto', { detail: img.id })); }}
              className={`shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${img.id === asset.id ? 'border-brand-400 opacity-100' : 'border-transparent opacity-40 hover:opacity-70'}`}
            >
              <img src={img.publicUrl ?? ''} alt={img.displayName} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}

// ── Asset card (grid) ─────────────────────────────────────────────────────────

function AssetCard({ asset, copied, onCopy, onDelete, onLightbox }: {
  asset: MediaAsset;
  copied: boolean;
  onCopy: () => void;
  onDelete: () => void;
  onLightbox: () => void;
}) {
  return (
    <div className="group bg-white rounded-xl border border-surface-200 overflow-hidden hover:border-brand-300 hover:shadow-md transition-all duration-200">
      {/* Preview */}
      <div className="relative aspect-square bg-surface-50 overflow-hidden">
        {isImage(asset) && asset.publicUrl ? (
          <img
            src={asset.publicUrl}
            alt={asset.displayName}
            className="w-full h-full object-cover cursor-zoom-in"
            loading="lazy"
            onClick={(e) => { e.stopPropagation(); onLightbox(); }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${fileIconColor(asset)}`}>
              <i className={`${fileIcon(asset)} text-2xl`} />
            </div>
            <span className="text-xs font-semibold text-surface-400 uppercase">.{asset.fileExtension || 'file'}</span>
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
          {isImage(asset) && asset.publicUrl && (
            <button
              onClick={(e) => { e.stopPropagation(); onLightbox(); }}
              title="Preview"
              className="w-9 h-9 rounded-xl bg-white/90 hover:bg-white text-surface-700 flex items-center justify-center transition-colors shadow-sm"
            >
              <i className="ri-zoom-in-line text-sm" />
            </button>
          )}
          {asset.publicUrl && (
            <button
              onClick={(e) => { e.stopPropagation(); onCopy(); }}
              title="Copy URL"
              className="w-9 h-9 rounded-xl bg-white/90 hover:bg-white text-surface-700 flex items-center justify-center transition-colors shadow-sm"
            >
              {copied ? <i className="ri-check-line text-emerald-600" /> : <i className="ri-link text-sm" />}
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="Delete"
            className="w-9 h-9 rounded-xl bg-white/90 hover:bg-red-50 text-red-500 flex items-center justify-center transition-colors shadow-sm"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
        {/* Badges */}
        <div className="absolute top-2 left-2 flex gap-1">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
            asset.visibility === 'public' ? 'bg-emerald-500 text-white' : 'bg-surface-700 text-white'
          }`}>
            {asset.visibility === 'public' ? 'PUB' : 'PRV'}
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="px-3 py-2.5">
        <p className="text-xs font-semibold text-surface-800 truncate" title={asset.displayName}>{asset.displayName}</p>
        <div className="flex items-center justify-between mt-0.5">
          <span className="text-[11px] text-surface-400">{fmtSize(asset.fileSize)}</span>
          {copied && <span className="text-[11px] text-emerald-600 font-medium">Copied!</span>}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminMedia() {
  const [assets, setAssets]         = useState<MediaAsset[]>([]);
  const [kind, setKind]             = useState<MediaUploadKind>('public-media');
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [queue, setQueue]           = useState<UploadItem[]>([]);
  const [view, setView]             = useState<'grid' | 'list'>('grid');
  const [search, setSearch]         = useState('');
  const [filterVis, setFilterVis]   = useState<'all' | 'public' | 'private'>('all');
  const [copiedId, setCopiedId]         = useState('');
  const [lightboxAsset, setLightboxAsset] = useState<MediaAsset | null>(null);
  const fileInputRef                    = useRef<HTMLInputElement>(null);
  const dragCounter                     = useRef(0);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      setAssets(await listMediaAssets());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load media');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const processFiles = useCallback(async (files: File[]) => {
    const items: UploadItem[] = files.map((f) => ({
      id: crypto.randomUUID(), name: f.name, size: f.size, status: 'pending',
    }));
    setQueue(items);

    for (let i = 0; i < files.length; i++) {
      setQueue((prev) => prev.map((item, idx) => idx === i ? { ...item, status: 'uploading' } : item));
      try {
        await uploadMediaAsset(files[i], kind);
        setQueue((prev) => prev.map((item, idx) => idx === i ? { ...item, status: 'done' } : item));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        setQueue((prev) => prev.map((item, idx) => idx === i ? { ...item, status: 'error', error: msg } : item));
      }
    }
    await refresh(true);
  }, [kind, refresh]);

  const handleCopy = (asset: MediaAsset) => {
    navigator.clipboard.writeText(asset.publicUrl ?? asset.storagePath);
    setCopiedId(asset.id);
    setTimeout(() => setCopiedId(''), 3000);
  };

  const handleDelete = async (asset: MediaAsset) => {
    if (!window.confirm(`Delete "${asset.displayName}"? This cannot be undone.`)) return;
    setError('');
    try {
      await deleteMediaAsset(asset);
      setAssets((prev) => prev.filter((a) => a.id !== asset.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete file');
    }
  };

  // Global drag events for the page
  const handleDragEnter = () => {
    dragCounter.current++;
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const filtered = assets.filter((a) => {
    if (filterVis !== 'all' && a.visibility !== filterVis) return false;
    const q = search.toLowerCase();
    return !q || a.displayName.toLowerCase().includes(q) || (a.productName ?? '').toLowerCase().includes(q);
  });

  const lightboxImages = filtered.filter((a) => isImage(a) && a.publicUrl);

  // Custom events from Lightbox prev/next/goto buttons
  useEffect(() => {
    const onPrev = () => {
      setLightboxAsset((cur) => {
        if (!cur) return null;
        const idx = lightboxImages.findIndex((a) => a.id === cur.id);
        return lightboxImages[idx - 1] ?? cur;
      });
    };
    const onNext = () => {
      setLightboxAsset((cur) => {
        if (!cur) return null;
        const idx = lightboxImages.findIndex((a) => a.id === cur.id);
        return lightboxImages[idx + 1] ?? cur;
      });
    };
    const onGoto = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      const found = lightboxImages.find((a) => a.id === id);
      if (found) setLightboxAsset(found);
    };
    const onKey = (e: KeyboardEvent) => {
      if (!lightboxAsset) return;
      if (e.key === 'ArrowLeft')  onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('lightbox-prev', onPrev);
    document.addEventListener('lightbox-next', onNext);
    document.addEventListener('lightbox-goto', onGoto);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('lightbox-prev', onPrev);
      document.removeEventListener('lightbox-next', onNext);
      document.removeEventListener('lightbox-goto', onGoto);
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxImages, lightboxAsset]);

  const images   = assets.filter(isImage).length;
  const files    = assets.filter((a) => !isImage(a)).length;
  const pubCount = assets.filter((a) => a.visibility === 'public').length;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">Media Library</h1>
          <p className="text-sm text-surface-500 mt-1">Upload and manage public images and private product files.</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors shrink-0 shadow-sm"
        >
          <i className="ri-upload-cloud-2-line" /> Upload Files
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          <i className="ri-error-warning-line shrink-0" /> {error}
        </div>
      )}

      {/* Stats */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Files', value: assets.length, icon: 'ri-folder-image-line', color: 'text-brand-600 bg-brand-50' },
            { label: 'Images',      value: images,         icon: 'ri-image-line',         color: 'text-violet-600 bg-violet-50' },
            { label: 'Other Files', value: files,          icon: 'ri-file-zip-line',      color: 'text-amber-600 bg-amber-50' },
            { label: 'Public',      value: pubCount,       icon: 'ri-global-line',        color: 'text-emerald-600 bg-emerald-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-surface-200 p-4">
              <div className={`w-8 h-8 rounded-lg ${s.color} flex items-center justify-center mb-2`}>
                <i className={`${s.icon} text-sm`} />
              </div>
              <p className="text-xl font-bold text-surface-900">{s.value}</p>
              <p className="text-xs text-surface-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      <DropZone
        kind={kind}
        onKindChange={setKind}
        isDragging={isDragging}
        onFiles={processFiles}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        inputRef={fileInputRef as React.RefObject<HTMLInputElement>}
      />

      {/* Upload queue */}
      <UploadQueue items={queue} onDismiss={() => setQueue([])} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 bg-white"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'public', 'private'] as const).map((v) => (
            <button key={v} onClick={() => setFilterVis(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${filterVis === v ? 'bg-brand-600 text-white' : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'}`}>
              {v}
            </button>
          ))}
          <div className="flex rounded-lg border border-surface-200 overflow-hidden">
            <button onClick={() => setView('grid')} className={`w-9 h-9 flex items-center justify-center transition-colors ${view === 'grid' ? 'bg-brand-600 text-white' : 'bg-white text-surface-500 hover:bg-surface-50'}`}>
              <i className="ri-grid-line text-sm" />
            </button>
            <button onClick={() => setView('list')} className={`w-9 h-9 flex items-center justify-center transition-colors ${view === 'list' ? 'bg-brand-600 text-white' : 'bg-white text-surface-500 hover:bg-surface-50'}`}>
              <i className="ri-list-check text-sm" />
            </button>
          </div>
        </div>
      </div>

      {/* Assets */}
      {loading ? (
        <div className="bg-white rounded-xl border border-surface-200 p-16 text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-surface-300 block mb-3" />
          <p className="text-sm text-surface-400">Loading media…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-surface-200 p-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-surface-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-image-line text-2xl text-surface-400" />
          </div>
          <p className="text-sm font-medium text-surface-700 mb-1">
            {assets.length === 0 ? 'No files uploaded yet' : 'No files match your filters'}
          </p>
          <p className="text-xs text-surface-400">Drag & drop files above to get started</p>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4">
          {filtered.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              copied={copiedId === asset.id}
              onCopy={() => handleCopy(asset)}
              onDelete={() => handleDelete(asset)}
              onLightbox={() => setLightboxAsset(asset)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50/60">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">File</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Type</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Size</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Visibility</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-surface-500 uppercase tracking-wide">Product</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((asset) => (
                  <tr key={asset.id} className="border-b border-surface-50 hover:bg-surface-50/50 transition-colors group">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {isImage(asset) && asset.publicUrl ? (
                          <img
                            src={asset.publicUrl}
                            alt={asset.displayName}
                            className="w-9 h-9 rounded-lg object-cover border border-surface-200 shrink-0 cursor-zoom-in hover:opacity-80 transition-opacity"
                            onClick={() => setLightboxAsset(asset)}
                          />
                        ) : (
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${fileIconColor(asset)}`}>
                            <i className={`${fileIcon(asset)} text-base`} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-surface-900 truncate max-w-[200px]">{asset.displayName}</p>
                          <p className="text-xs text-surface-400 truncate max-w-[200px]">{asset.storagePath}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface-100 text-surface-600 text-xs font-medium uppercase">
                        {asset.fileExtension || 'file'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-surface-500">{fmtSize(asset.fileSize)}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${asset.visibility === 'public' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-surface-100 text-surface-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${asset.visibility === 'public' ? 'bg-emerald-500' : 'bg-surface-400'}`} />
                        {asset.visibility}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-surface-500">{asset.productName ?? '—'}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {isImage(asset) && asset.publicUrl && (
                          <button onClick={() => setLightboxAsset(asset)} title="Preview"
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-colors">
                            <i className="ri-zoom-in-line text-sm" />
                          </button>
                        )}
                        {asset.publicUrl && (
                          <button onClick={() => handleCopy(asset)} title="Copy URL"
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-400 hover:text-surface-700 transition-colors">
                            {copiedId === asset.id ? <i className="ri-check-line text-emerald-600" /> : <i className="ri-link text-sm" />}
                          </button>
                        )}
                        <button onClick={() => handleDelete(asset)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-surface-400 hover:text-red-500 transition-colors">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxAsset && isImage(lightboxAsset) && (
        <Lightbox
          asset={lightboxAsset}
          images={lightboxImages}
          copied={copiedId === lightboxAsset.id}
          onClose={() => setLightboxAsset(null)}
          onCopy={handleCopy}
          onDelete={(a) => { handleDelete(a); setLightboxAsset(null); }}
        />
      )}
    </div>
  );
}
